import { Box, ButtonBase, Skeleton, Stack, Theme } from '@mui/material';
import { memo, ReactNode, useCallback, useState } from 'react';
import { GiornoFascia, StatoDati } from '../../hooks/usePresenzeGiorni';
import { formatGiornoEsteso } from '../../utility_objects/PresenzeUtils';
import { PopoverPresenze } from './PopoverPresenze';

/**
 * Contorno della cella di un giorno della fascia Presenze: stile di base, Skeleton
 * delle colonne non ancora misurate, etichetta accessibile e pulsante che apre il
 * popover col dettaglio.
 */

/** Skeleton delle righe, come in FOCUS CLOUD. */
export const ALTEZZA_SKELETON = 12;
// Due righe da 20px nei 48px minimi della cella (anche touch target).
const ALTEZZA_RIGA_SKELETON = 20;

const stileBase = (bordo: boolean) =>
  ({
    minWidth: 0,
    boxSizing: 'border-box',
    px: 0.75,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: bordo ? '1px solid var(--divider-color)' : undefined,
  } as const);

const stileRigaSkeleton = {
  justifyContent: 'center',
  height: ALTEZZA_RIGA_SKELETON,
  minWidth: 0,
} as const;

/**
 * Cella in caricamento, alta 48px in tutte e due le modalità: colonne non ancora
 * misurate (cambio di vista), con le due righe come il caricamento dei dati.
 */
export const CellaPresenzeSkeleton = memo(function CellaPresenzeSkeleton({
  bordo,
}: {
  bordo: boolean;
}): JSX.Element {
  return (
    <Box sx={{ ...stileBase(bordo), height: 48, justifyContent: 'center' }}>
      <Stack sx={stileRigaSkeleton}>
        <Skeleton variant="rounded" width="70%" height={ALTEZZA_SKELETON} />
      </Stack>
      <Stack sx={stileRigaSkeleton}>
        <Skeleton variant="rounded" width="50%" height={ALTEZZA_SKELETON} />
      </Stack>
    </Box>
  );
});

// Es. "sabato 18 ottobre: 6 in servizio, 4 assenti, 1 assente in parte".
const getAriaLabel = (
  giorno: GiornoFascia,
  statoAssenze: StatoDati
): string => {
  const parti: string[] = [];
  if (giorno.inServizio) parti.push(`${giorno.inServizio.length} in servizio`);
  else
    parti.push(
      giorno.statoInServizio === 'errore'
        ? 'in servizio non disponibile'
        : 'in servizio in caricamento'
    );
  if (giorno.assenti) {
    const interi = giorno.assenti.filter((a) => a.tuttoIlGiorno).length;
    const parziali = giorno.assenti.length - interi;
    parti.push(`${interi} ${interi === 1 ? 'assente' : 'assenti'}`);
    if (parziali > 0)
      parti.push(
        `${parziali} ${parziali === 1 ? 'assente' : 'assenti'} in parte`
      );
  } else
    parti.push(
      statoAssenze === 'errore'
        ? 'assenze non disponibili'
        : 'assenze in caricamento'
    );
  return `${formatGiornoEsteso(giorno.giorno)}: ${parti.join(', ')}`;
};

interface CellaCliccabileProps {
  giorno: GiornoFascia;
  statoAssenze: StatoDati;
  /** Divisoria a sinistra, come fra le colonne dell'intestazione. */
  bordo: boolean;
  children: ReactNode;
}

/**
 * Cella di un giorno come pulsante: tutta la cella apre il popover con l'elenco
 * completo, e il nome accessibile riassume i conteggi del giorno. Alta almeno 48px,
 * cresce quanto il contenuto, che parte in alto. Disabilitata finché non c'è nessun
 * dato.
 */
export const CellaCliccabile = ({
  giorno,
  statoAssenze,
  bordo,
  children,
}: CellaCliccabileProps): JSX.Element => {
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  // Stabile, così il memo del popover non si perde a ogni render della cella.
  const chiudiPopover = useCallback(() => setAncora(null), []);

  return (
    <>
      <ButtonBase
        component="div"
        aria-haspopup="dialog"
        aria-label={getAriaLabel(giorno, statoAssenze)}
        disabled={giorno.inServizio === null && giorno.assenti === null}
        onClick={(event) => setAncora(event.currentTarget)}
        sx={{
          ...stileBase(bordo),
          minHeight: 48,
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          py: 0.5,
          gap: 0.5,
          textAlign: 'left',
          '&:hover': { bgcolor: 'action.hover' },
          // Il focus da tastiera resta visibile (ButtonBase toglie l'outline).
          '&.Mui-focusVisible': {
            outline: (theme: Theme) =>
              `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '-2px',
          },
        }}
      >
        {children}
      </ButtonBase>
      <PopoverPresenze
        anchorEl={ancora}
        onClose={chiudiPopover}
        giorno={giorno}
        statoAssenze={statoAssenze}
      />
    </>
  );
};
